import { Link, useLocation } from "react-router-dom";
import { Home, PlusCircle, BarChart3, Settings, Zap, CalendarDays, ArrowUpCircle, Moon, Sun, Monitor, FlaskConical } from "lucide-react";
import { motion } from "framer-motion";
import { useProfile } from "@/hooks/useProfile";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/checkin", label: "Check-in", icon: PlusCircle },
  { to: "/plan", label: "Daily plan", icon: CalendarDays },
  { to: "/insights", label: "Insights", icon: BarChart3 },
  { to: "/analytics", label: "Analytics", icon: FlaskConical },
  { to: "/settings", label: "Settings", icon: Settings },
];

export const DesktopSidebar = () => {
  const location = useLocation();
  const { profile } = useProfile();
  const { theme, resolved, setTheme } = useTheme();

  const cycleTheme = () => {
    const order: Array<"light" | "dark" | "system"> = ["light", "dark", "system"];
    const next = order[(order.indexOf(theme) + 1) % 3];
    setTheme(next);
  };

  const ThemeIcon = theme === "system" ? Monitor : resolved === "dark" ? Moon : Sun;
  const themeLabel = theme === "system" ? "System" : resolved === "dark" ? "Dark" : "Light";

  return (
    <aside className="hidden md:flex flex-col w-64 border-r border-border/60 bg-card/80 backdrop-blur-sm min-h-screen p-5" role="navigation" aria-label="Main navigation">
      <Link to="/dashboard" className="flex items-center gap-2.5 font-heading font-bold text-[17px] mb-8 px-1">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Zap className="h-4 w-4 text-primary" />
        </div>
        BS&nbsp;of&nbsp;MS™
      </Link>

      {profile?.first_name && (
        <div className="px-1 mb-6">
          <p className="text-[13px] text-muted-foreground">Welcome back,</p>
          <p className="text-sm font-semibold">{profile.first_name}</p>
        </div>
      )}

      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          return (
            <Link key={item.to} to={item.to}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-colors ${
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:bg-muted/80 hover:text-foreground"
              }`}>
              {active && (
                <motion.div
                  layoutId="sidebar-active"
                  className="absolute inset-0 bg-primary/10 rounded-xl shadow-sm"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-3">
                <item.icon className={`h-[18px] w-[18px] ${active ? "text-primary" : ""}`} />
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Evening reflection nudge */}
      {new Date().getHours() >= 17 && (
        <Link to="/reflection"
          className="flex items-center gap-2 px-3 py-2.5 mb-3 rounded-xl text-[13px] font-medium text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-all">
          <Moon className="h-[18px] w-[18px]" />
          Evening Reflection
        </Link>
      )}

      {/* Theme toggle */}
      <button
        onClick={cycleTheme}
        className="flex items-center gap-3 px-3 py-2.5 mb-3 rounded-xl text-[13px] font-medium text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-all w-full"
        aria-label={`Switch theme, currently ${themeLabel}`}
      >
        <ThemeIcon className="h-[18px] w-[18px]" />
        {themeLabel} mode
      </button>

    </aside>
  );
};

export const MobileTabBar = () => {
  const location = useLocation();

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 glass z-50 safe-area-bottom" aria-label="Mobile navigation">
      <div className="flex items-center justify-around h-[4.5rem] px-1">
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          const isCheckin = item.to === "/checkin";
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`relative flex flex-col items-center justify-center text-[11px] font-medium min-w-[3.5rem] min-h-[3.5rem] rounded-2xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                active ? "text-primary" : "text-muted-foreground"
              }`}
              aria-current={active ? "page" : undefined}
            >
              {isCheckin ? (
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center -mt-3 shadow-lg active:scale-95 transition-transform ${
                    active ? "bg-primary text-primary-foreground" : "bg-primary/90 text-primary-foreground"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                </div>
              ) : (
                <>
                  <item.icon className={`h-5 w-5 mb-0.5 ${active ? "text-primary" : ""}`} />
                  {active && (
                    <span className="absolute bottom-1.5 w-4 h-0.5 bg-primary rounded-full" />
                  )}
                </>
              )}
              <span className={isCheckin ? "mt-1" : ""}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
